import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";

export type AutoTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  minRows?: number;
};

/** A native textarea that grows with its content without shrinking below its usable baseline. */
export const AutoTextarea = forwardRef<HTMLTextAreaElement, AutoTextareaProps>(
  function AutoTextarea(
    { className, minRows = 3, onInput, value, ...props },
    forwardedRef
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const resize = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, []);

    useLayoutEffect(() => {
      resize();
    }, [resize, value]);

    const setRef = (element: HTMLTextAreaElement | null) => {
      textareaRef.current = element;
      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    };

    return (
      <textarea
        ref={setRef}
        rows={minRows}
        value={value}
        onInput={(event) => {
          resize();
          onInput?.(event);
        }}
        className={cn(
          "flex min-h-11 w-full resize-none rounded-2xl border border-input bg-surface-raised px-4 py-3",
          "text-base leading-relaxed text-foreground placeholder:text-foreground-muted",
          "shadow-sm transition-[box-shadow,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    );
  }
);
